import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, loginUserWithGoogle } from "../apis/auth";
import { toast } from "react-toastify";
import { LogIn, AlertCircle, Shield, Eye, EyeOff } from "lucide-react";
import bluTecPingLogo from "../assets/i1.png"; // <-- your logo file
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "BluTec PING Login — Access Your WhatsApp Growth Dashboard";
    const description =
      "Securely sign in to BluTec PING to run WhatsApp broadcasts, automate chat, and track performance in real time.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await loginUser(form);
      if (response.data.success === false) {
        throw new Error(response.data.error || "Login failed.");
      } else {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("user_id", JSON.stringify(response.data.user.id));
        localStorage.setItem("userAccessToken", response.data.token);
        toast.success(response.data.message || "Login successful!");
        setTimeout(() => navigate("/dashboard"), 1000);
      }
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Login failed.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setIsGoogleLoading(true);
    try {
      const data = { token: credentialResponse.credential };
      const response = await loginUserWithGoogle(data);
      if (response.data.success) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("user_id", JSON.stringify(response.data.user.id));
        localStorage.setItem("userAccessToken", response.data.token);
        toast.success(response.data.message || "Google sign-in successful!");
        setTimeout(() => navigate("/dashboard"), 1000);
      } else {
        toast.error(response.data.error || "Google Login failed.");
      }
    } catch (err) {
      toast.error("Google Login failed. Try again.");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const googleClientId = process.env.REACT_APP_CLIENT_ID;

  const shouldShowForgotPassword = () => {
    if (!error) return false;
    const lowered = error.toLowerCase();
    return ["password", "credential", "invalid"].some((k) =>
      lowered.includes(k)
    );
  };

  return (
    <div className="h-screen w-full bg-white text-gray-900 flex flex-col items-center justify-center px-4 py-10 sm:py-16 overflow-hidden">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-6">
          <img
            src={bluTecPingLogo}
            alt="BluTec PING"
            className="h-16 mx-auto mb-2" // adjusted for prominent, crisp logo
          />
          <h1 className="mt-1 text-4xl font-extrabold leading-tight text-[#01CC54]">
            Welcome Back
          </h1>
          <p className="text-gray-600 mt-1">
            Sign in to your WhatsApp growth dashboard
          </p>
        </div>

        {/* Card */}
        <div className="relative rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#048AAD] via-[#048AAD]/90 to-[#048AAD]" />
          <div className="p-7">
            {/* Error */}
            {error && (
              <div className="mb-5 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{error}</p>
                    {shouldShowForgotPassword() && (
                      <button
                        onClick={() => {
                          if (!form.email) {
                            toast.error(
                              "Please enter your email to reset password"
                            );
                            return;
                          }
                          navigate("/forget-password", {
                            state: { email: form.email },
                          });
                        }}
                        className="mt-1 text-sm text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-2"
                        type="button"
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Security chip */}
            <div className="flex items-center justify-center mb-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#01CC54]/20 bg-[#01CC54]/10 px-3 py-1.5 text-[#01CC54] text-xs font-medium">
                <Shield size={14} />
                Secure authentication
              </span>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="mb-1 block text-sm font-medium text-gray-600"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1 block text-sm font-medium text-gray-600"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-base text-gray-900 placeholder-gray-500 shadow-sm outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54]"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full rounded-xl py-3 text-lg font-semibold text-white bg-[#048AAD] shadow-lg transition-transform hover:-translate-y-0.5 hover:shadow-xl hover:bg-[#048AAD]/90 focus:outline-none focus:ring-2 focus:ring-[#048AAD]/30 ${
                  isLoading ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <LogIn size={18} />
                    <span>Sign In</span>
                  </div>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center my-6">
              <hr className="flex-grow border-t border-gray-200" />
              <span className="mx-3 text-gray-500 text-sm">OR</span>
              <hr className="flex-grow border-t border-gray-200" />
            </div>

            {/* Google */}
            <div className="flex justify-center mt-3 mb-2">
              <GoogleOAuthProvider clientId={googleClientId}>
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => toast.error("Google Login Failed")}
                  theme="outline"
                  text="continue_with"
                  width="290px"
                  shape="pill"
                  size="large"
                  logo_alignment="center"
                  disabled={isGoogleLoading}
                />
              </GoogleOAuthProvider>
            </div>

            {/* Signup */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <a
                  href="/signup"
                  className="font-semibold text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-4"
                >
                  Sign up
                </a>
              </p>
              <p className="text-gray-500 text-sm py-2">
            Trouble signing in? Contact{" "}
            <a
              href="mailto:sales@blutec.ai"
              className="text-[#01CC54] hover:text-[#01CC54]/80 underline underline-offset-2 font-semibold transition"
            >
              sales@blutec.ai
            </a>
          </p>
            </div>
          </div>
        </div>

        {/* Help */}
        <div className="mt-6 text-center">
          
        </div>
      </div>
    </div>
  );
};

export default Login;
